import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import useAnchoredRect, { clampToViewport } from '../../hooks/useAnchoredRect';
import useOutsideClose from '../../hooks/useOutsideClose';

import { TEMPLATE_CATEGORIES, fieldTemplateRows } from '../../utils/rxTemplates';
import TemplateSuggestionRow, {
  TemplateGroupHeading, TemplateSeeAllRow,
} from './TemplateSuggestionRow';
import MemorySuggestionRow from './MemorySuggestionRow';
import {
  MEMORY_TYPES, memoryFieldRows, memoryGroupHeading, memoryCountLine, filterMemoryRows,
  TEST_TYPE_PLACEHOLDER, TEST_OUTSIDE_HINT, MEMORY_FORGET_ARIA,
} from '../../utils/rxMemory';
import { normalizeLabName, findCatalogMatch } from './LabPanel';

/**
 * "Add from lab list" — a TEXT-ONLY picker over the org's lab catalogue.
 *
 * Owner ruling (rx-r3): a test on the doctor's Tests row is PLAIN TEXT on the
 * prescription plus a dropdown that inserts a catalogue test's NAME as text.
 * No lab order is created from this surface by any path — reception raises the
 * orders.
 *
 * FE-5 (owner ruling Q2b) — the search box also accepts free typing: Enter
 * with nothing highlighted commits the TYPED TEXT itself (`findCatalogMatch`
 * decides catalogue-name-as-text vs raw-text-as-outside-test), one box, two
 * behaviours — it still only ever calls `onPick`, never `advise`.
 *
 * This component is therefore PURELY PRESENTATIONAL and deliberately does NOT
 * import `useLabOrderStore`: `advise` is absent from its module graph, so lab
 * ordering is unreachable from here BY CONSTRUCTION, not by a guard.
 *
 * It also owns NO data fetching — `labTests` is the dept-scoped catalogue slice
 * the parent (LabTestsField / LabPanel) already fetched. Nothing here can fire
 * a request, so it cannot contribute to a request storm.
 *
 * Props:
 *   labTests      — dept-scoped lab catalogue rows ({ procedure_id, name, code }).
 *   existingNames — names already on the visit's Tests row (single-source
 *                   `visitTestProjection().printableNames`); these are not
 *                   offered again.
 *   onPick        — (test) => void. The parent appends `test.name` as text.
 *   isDark        — theme flag (explicit day/night surfaces, per CLAUDE.md).
 *   templates     — D25/F6, OPTIONAL and absent everywhere but the doctor's pad:
 *                   { selection, narrow, onAdd, onSeeAll }. The doctor's LAB
 *                   TESTS templates are offered as rows ABOVE the catalogue
 *                   chips, narrowed by the same search box, with `↓` walking
 *                   into them. Adding one appends test NAMES exactly as picking
 *                   a chip does — this component still cannot place an order.
 */

// Mirrors the catalogue picker cap used by QuickSearchAdd (`matches`): at most
// 8 rows, and the top 8 when the query is empty.
const MAX_RESULTS = 8;

export default function LabListPicker({
  labTests, existingNames, onPick, isDark = false, templates = null,
  // FE-4 — the G1 `memory` object (`memory.owner`, `memory.types.INV`).
  // Absent (null) for every mount but the doctor's pad, exactly like
  // `templates` — the parent decides who gets memory chrome (D12), this
  // component just renders nothing when it is null.
  memory = null,
  // FE-12 — forget a remembered test (G5). `onForgetMemory(row)` — absent
  // (null) renders no forget × at all (every mount but the doctor's pad,
  // same posture as `memory`/`templates`); this component owns none of the
  // confirm/toast UI, it only asks the parent to open it.
  onForgetMemory = null,
  finePointer = true,
}) {
  const [query, setQuery] = useState('');
  // D25 — nothing is highlighted until the doctor presses ↓, in every field.
  const [highlight, setHighlight] = useState(-1);
  const inputId = useId();
  // FE-23 — the panel is anchored to THIS field's input, exactly like the
  // medicine and diagnosis panels. In the two-column pad the Tests field sits
  // in the RIGHT column, and an in-flow panel took the column's left edge as
  // its origin: it opened shifted left and was cut off.
  const searchRef = useRef(null);
  const panelRef = useRef(null);
  const fieldRef = useRef(null);
  const seeAllRef = useRef(null);
  // FE-25 (owner live run) — the panel had no way out: it rendered while the
  // field held templates and only went away once a test was picked. It now
  // opens on FOCUS and closes the three ways every other field panel closes:
  // Esc (focus returns to the input), a click/tap outside it, and focus
  // leaving the field. Re-focusing the input reopens it; picking nothing is
  // fine.
  const [open, setOpen] = useState(false);
  const close = useCallback(() => { setOpen(false); setHighlight(-1); }, []);
  useOutsideClose(open, [panelRef, fieldRef], close);

  // FE-27 — when the All-templates list was opened FROM this field, closing it
  // (Esc, ✕, a click away) hands the doctor back to the Tests input he came
  // from. The list itself cannot do it: it returns focus to the control that
  // opened it, and that control lives in a panel this field has since closed.
  // FE-28 — a row warning appeared on the pad (a medicine or a test the doctor
  // has to look at): this panel closes so it cannot cover it. It comes back the
  // normal way — a keystroke here, or leaving the field and returning.
  const closeSignal = templates?.closeSignal ?? 0;
  const seenCloseSignalRef = useRef(closeSignal);
  useEffect(() => {
    if (closeSignal === seenCloseSignalRef.current) return;
    seenCloseSignalRef.current = closeSignal;
    close();
  }, [closeSignal, close]);

  const listOpen = !!templates?.listOpen;
  const openedTheListRef = useRef(false);
  const wasListOpenRef = useRef(false);
  useEffect(() => {
    if (wasListOpenRef.current && !listOpen && openedTheListRef.current) {
      openedTheListRef.current = false;
      searchRef.current?.focus();
    }
    wasListOpenRef.current = listOpen;
  }, [listOpen]);

  // The template rows come from THE shared per-field selector — same ordering,
  // same cap, same `See all n` number as every other surface. `templates` is
  // absent (null) for every mount but the doctor's pad, so this is simply an
  // empty group everywhere else. Depends on the SELECTION object (a `useMemo`
  // in the pad), never on the wrapper prop, which is rebuilt every render.
  const templateSelection = templates?.selection || null;
  const templateNarrow = !!templates?.narrow;
  const templateGroup = useMemo(
    () => fieldTemplateRows(templateSelection, TEMPLATE_CATEGORIES.LAB_TESTS, query, { narrow: templateNarrow }),
    [templateSelection, query, templateNarrow],
  );
  const templateRows = templateSelection ? templateGroup.rows : [];

  // Already on the Tests row → not offered again (same shape as the catalogue
  // picker it replaces, which filtered out tests already advised on the visit).
  // Computed early — the memory group below needs it too.
  const taken = useMemo(
    () => new Set((existingNames || []).map(normalizeLabName)),
    [existingNames],
  );

  // FE-4 — the doctor's own remembered tests (`memory.types.INV`), ranked,
  // ONE per identity, minus anything already on this visit's Tests row (the
  // same `taken` set the catalogue chips are filtered against, so a test
  // already added never re-appears from either source). `memRowsAll` (query
  // UNFILTERED) drives the catalogue no-duplicates exclusion below — a
  // memory-shadowed catalogue row must stay hidden no matter what is typed,
  // not just while the typed text happens to also match the memory row.
  const memRowsAll = useMemo(() => {
    if (!memory) return [];
    const ranked = memoryFieldRows(memory, MEMORY_TYPES.INV).rows;
    return ranked.filter((r) => !taken.has(normalizeLabName(r.v)));
  }, [memory, taken]);
  const memRows = useMemo(() => filterMemoryRows(memRowsAll, query), [memRowsAll, query]);
  const memHeading = memRows.length > 0 ? memoryGroupHeading(MEMORY_TYPES.INV, memory?.owner) : null;
  // Owner ruling — the count line reports what is actually RENDERED: both
  // numbers already exclude anything taken (already on the visit).
  const memCountLine = memHeading ? memoryCountLine(memRowsAll.length, memRows.length, query) : null;

  // A narrow host (the Visit Actions accordion, mobile) keeps the panel IN
  // FLOW, as it ships today — there is no column to escape there, and a fixed
  // panel over a 340px pad would cover the row it belongs to.
  // An org with NO lab catalogue renders no search box (see the early return
  // below), so there is nothing to focus: the group stays put there, or the
  // doctor's own lab templates would be unreachable.
  const hasSearchBox = !!(labTests && labTests.length > 0);
  const showPanel = (templateRows.length > 0 || memRows.length > 0) && (open || !hasSearchBox);
  // bk-986 — float ONLY when there is something to anchor to. With no
  // catalogue there is no search box, so a `fixed` panel had nothing to
  // measure and painted at the viewport's top-left corner, permanently (the
  // focus gate does not apply in that branch either). No anchor → stay in
  // flow, where the doctor can actually read it.
  const wantsFloat = showPanel && !templateNarrow && hasSearchBox;
  const anchor = useAnchoredRect(wantsFloat, searchRef);
  const placed = wantsFloat
    ? clampToViewport(anchor, {
      width: anchor?.width,
      height: panelRef.current?.getBoundingClientRect().height || 0,
    })
    : null;
  // …and it only PAINTS floating once it has a placement: no anchor (no
  // catalogue) and no first measurement yet both render in flow rather than at
  // the viewport's corner.
  const floating = !!placed;

  // FIX-4 (owner, live click-through) — REGRESSION traced to FE-4 (9e3bd66):
  // widening `showPanel` to include memory rows means this panel now shows
  // for ANY doctor who has ever typed one lab test, not only orgs with a
  // LAB_TESTS template — and once `position: fixed` (above), the panel takes
  // NO flow height, so the catalogue chips `<ul>` below it renders at the
  // SAME on-screen coordinates the panel paints over (same anchor, same top).
  // The panel's solid background + `zIndex: 1000` then intercepts every
  // click meant for a chip underneath it — clicks on "+ CT Head" etc. landed
  // on the (invisible-to-the-click, since it decides nothing there) panel
  // area and did nothing. Fix: reserve the panel's OWN measured height as a
  // spacer in flow, so the chips are pushed below its footprint — the panel
  // never captures a click outside its own rows, and the chips stay
  // clickable whether the panel is open or closed.
  const [panelHeight, setPanelHeight] = useState(0);
  useEffect(() => {
    const h = floating ? (panelRef.current?.getBoundingClientRect().height || 0) : 0;
    setPanelHeight((prev) => (prev === h ? prev : h));
  }, [floating, templateRows.length, memRows.length]);

  // bk-989 — ↓ walks the template rows, then `See all`, then FE-5 extends the
  // walk into the doctor's own remembered tests below — so EVERY row this
  // panel renders is reachable without a mouse, closing the gap FE-4 left
  // (memory rows were Tab/click-only). bk-988 — it is here while typing, and
  // carries the typed text into the list.
  const seeAllCount = showPanel ? templateGroup.seeAll : 0;
  const templateWalkLength = templateRows.length + (seeAllCount > 0 ? 1 : 0);
  const seeAllIndex = seeAllCount > 0 ? templateWalkLength - 1 : -1;
  const walkLength = templateWalkLength + memRows.length;

  const onSearchKeyDown = (e) => {
    // Esc closes the panel and leaves the doctor in the box he was typing in —
    // the same contract the medicine and diagnosis panels hold.
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      searchRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown') {
      if (walkLength === 0 || !open) return;
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, walkLength - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      if (walkLength === 0 || !open) return;
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, -1));
      return;
    }
    if (e.key !== 'Enter') return;
    if (highlight >= 0 && walkLength > 0 && open) {
      // A row is highlighted — Enter picks IT, same as clicking it.
      e.preventDefault();
      if (highlight === seeAllIndex) {
        openedTheListRef.current = true;
        templates.onSeeAll(seeAllRef.current, query.trim());
      } else if (highlight < templateWalkLength) {
        templates.onAdd(templateRows[highlight]);
      } else {
        const memRow = memRows[highlight - templateWalkLength];
        if (memRow) onPick({ name: memRow.v });
      }
      setHighlight(-1);
      return;
    }
    // FE-5 — nothing highlighted: Enter commits the TYPED TEXT itself. A
    // catalogue match (`findCatalogMatch`, alias-aware) resolves to the
    // catalogue's canonical name via `onPick` — text only, no lab order
    // (owner ruling Q2b/rx-r3: this component never imports
    // `useLabOrderStore`). No match appends the raw text the same way — the
    // Tests row's existing `fromLabList` check then badges it "Outside".
    // `onPick`/`pickFromLabList` already dedupe on `normalizeLabName`.
    const text = query.trim();
    if (!text) return;
    e.preventDefault();
    const match = findCatalogMatch(text, labTests);
    onPick(match || { name: text });
    setQuery('');
    setHighlight(-1);
  };

  // Focus ENTERING the field opens the panel; focus LEAVING it closes. React's
  // focus/blur bubble, so one pair on the field covers the input, the template
  // rows and `See all` — and `relatedTarget` inside the field (a row, the
  // opener) is a move WITHIN it, not an exit.
  //
  // FIX-8 (owner) — this div also wraps the ALWAYS-VISIBLE catalogue chips
  // `<ul>`, so a plain click on a "+ CT Head" chip moved focus to that
  // button (a browser default) and bubbled into this SAME `onFocus`,
  // opening the memory/template panel on top of a click that should only
  // add the test. Gate on the REAL target: only the search input itself
  // opens the panel; a chip (or anything else) receiving focus is not "the
  // field," it is a sibling list this field happens to share a wrapper
  // with — no behaviour change for Tab/click INTO an already-open panel's
  // rows or `See all` (that only ever happens while `open` is already true).
  const onFieldFocus = (e) => { if (e.target === searchRef.current) setOpen(true); };
  const onFieldBlur = (e) => {
    const next = e.relatedTarget;
    // FE-27 — a blur with NO relatedTarget is not an exit. Browsers fire one
    // mid-press (Safari and Firefox do not focus a pressed button), and
    // closing on it took the panel — and the `See all` the doctor was pressing
    // — out of the DOM before the click landed. "Clicked somewhere else" is
    // already covered by the outside-mousedown rule, which is the honest
    // signal for it.
    if (!next) return;
    if (fieldRef.current?.contains(next) || panelRef.current?.contains(next)) return;
    close();
  };

  // MemorySuggestionRow is styled with plain values (font-size/colour
  // strings), not the Tailwind classes the rest of this file uses — this
  // component is the ONE place the shared row renders inside a Tailwind
  // surface, so it gets its own small equivalents rather than a signature
  // change to the shared component.
  const memRowType = { row: '0.8125rem', hint: '0.6875rem' };
  const memRowInk = isDark ? '#f1f5f9' : '#0f172a';
  const memRowInkFaint = isDark ? '#94a3b8' : '#64748b';

  // D25/F6 + FE-4 — ONE dropdown, template rows then the doctor's own
  // remembered tests (mockup variant A, same order every other field uses).
  // Either group alone is enough to show the panel (`showPanel`, above).
  const templateBlock = showPanel ? (
    <div
      data-testid="lab-template-group"
      ref={panelRef}
      data-floating={floating ? 'true' : 'false'}
      style={floating ? {
        // Rect-anchored `fixed`: a fixed box's containing block is the
        // viewport, so neither the pad's `overflow: hidden` nor the column can
        // clip or shift it — the same escape the medicine popover uses, with
        // no portal, so the panel keeps its place in the DOM (and in the
        // reading order) above the catalogue chips.
        position: 'fixed',
        top: placed.top, left: placed.left,
        width: anchor?.width || undefined,
        zIndex: 1000,
        background: isDark ? '#1f2937' : '#ffffff',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.16)' : 'rgba(15,23,42,0.18)'}`,
        borderRadius: '0.5rem',
        boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
        maxHeight: 'min(20rem, 50vh)', overflowY: 'auto',
      } : undefined}
    >
      {templateRows.length > 0 && (
        <>
          <TemplateGroupHeading label={templateGroup.heading} isDark={isDark} />
          {templateRows.map((t, i) => (
            <TemplateSuggestionRow
              key={t.template_id}
              template={t}
              category={TEMPLATE_CATEGORIES.LAB_TESTS}
              narrow={templateNarrow}
              highlighted={i === highlight}
              isDark={isDark}
              onPick={() => { templates.onAdd(t); setHighlight(-1); }}
            />
          ))}
          {templateGroup.seeAll > 0 && (
            <TemplateSeeAllRow
              count={templateGroup.seeAll}
              category={TEMPLATE_CATEGORIES.LAB_TESTS}
              isDark={isDark}
              highlighted={highlight === seeAllIndex}
              rowRef={seeAllRef}
              onClick={(e) => {
                openedTheListRef.current = true;
                templates.onSeeAll(e.currentTarget, query.trim());
              }}
            />
          )}
        </>
      )}
      {memRows.length > 0 && (
        <div data-testid="lab-memory-group">
          {memRows.map((r, i) => (
            <MemorySuggestionRow
              key={`lab-mem-${i}`}
              label={r.v}
              groupHeading={i === 0 ? memHeading : undefined}
              countLine={i === 0 ? memCountLine : undefined}
              startsGroup={i === 0}
              // FE-5 — closes FE-4's documented gap: memory rows now sit at
              // the TAIL of the same ↓/↑ walk the template rows use.
              highlighted={highlight === templateWalkLength + i}
              isDark={isDark}
              type={memRowType}
              ink={memRowInk}
              inkFaint={memRowInkFaint}
              // A memory row is exactly a `{name}`-shaped "test" to `onPick` —
              // the same append-to-`investigations[]`, no-order path a
              // catalogue pick already uses (rx-r3 unaffected).
              onClick={() => { onPick({ name: r.v }); setHighlight(-1); }}
              finePointer={finePointer}
              forgetAriaLabel={MEMORY_FORGET_ARIA.replace('{name}', r.v)}
              onForget={onForgetMemory ? () => onForgetMemory(r) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  ) : null;

  // Catalogue rows already offered as memory are DROPPED here (owner ruling —
  // no duplicates: memory wins). `memRowsAll` (not the query-narrowed
  // `memRows`) so a shadowed catalogue row stays hidden regardless of what is
  // typed.
  const memoryNameSet = useMemo(
    () => new Set(memRowsAll.map((r) => normalizeLabName(r.v))),
    [memRowsAll],
  );

  const available = useMemo(
    () => (labTests || []).filter(
      (t) => !taken.has(normalizeLabName(t.name)) && !memoryNameSet.has(normalizeLabName(t.name)),
    ),
    [labTests, taken, memoryNameSet],
  );

  // Same filter/cap shape as QuickSearchAdd's `matches`: empty query → top 8,
  // otherwise substring match, capped at 8. Matching is normalized (lowercase,
  // punctuation-stripped) via the shared `normalizeLabName` so "cbc"/"C.B.C."
  // and a catalogue `code` both resolve.
  const matches = useMemo(() => {
    const q = normalizeLabName(query);
    if (!q) return available.slice(0, MAX_RESULTS);
    return available
      .filter((t) =>
        normalizeLabName(t.name).includes(q)
        || normalizeLabName(t.code).includes(q),
      )
      .slice(0, MAX_RESULTS);
  }, [query, available]);

  const labelCls = isDark ? 'text-slate-200' : 'text-slate-700';
  const mutedCls = isDark ? 'text-slate-400' : 'text-slate-500';
  const fieldCls = isDark
    ? 'bg-slate-800 border-slate-600 text-slate-100 placeholder-slate-400'
    : 'bg-white border-slate-300 text-slate-800 placeholder-slate-400';
  const rowCls = isDark
    ? 'bg-slate-800 text-slate-100 hover:bg-slate-700'
    : 'bg-white text-slate-800 hover:bg-slate-100';

  // Empty catalogue — the org has no lab tests at all. Typing still works, and
  // the doctor's own lab-test templates are still offered (they are text, not
  // catalogue entries).
  if (!labTests || labTests.length === 0) {
    return (
      <div className="flex flex-col gap-1" ref={fieldRef} onFocus={onFieldFocus} onBlur={onFieldBlur}>
        <span className={`text-xs font-semibold ${labelCls}`}>Add from lab list</span>
        {templateBlock}
        <span className={`text-xs italic ${mutedCls}`}>
          No tests in your lab list yet. Type the test name instead.
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1" ref={fieldRef} onFocus={onFieldFocus} onBlur={onFieldBlur}>
      <label htmlFor={inputId} className={`text-xs font-semibold ${labelCls}`}>
        Add from lab list
        {/* the shortcut's own hint, in this field's existing label line and
            only while it is actually available (empty box, fine pointer) */}
        {templates?.finePointer && query === '' && open && (
          <span data-testid="double-click-hint" className={`ml-2 font-normal ${mutedCls}`}>
            {templates.doubleClickHint}
          </span>
        )}
      </label>
      <input
        id={inputId}
        type="text"
        value={query}
        ref={searchRef}
        onChange={(e) => { setQuery(e.target.value); setHighlight(-1); }}
        onKeyDown={onSearchKeyDown}
        // D41/S17 — the pad's ONE double-click rule, applied to this field's
        // own input: empty only, fine pointer only. Closing this panel is the
        // field's own business, so it happens here.
        onDoubleClick={(e) => {
          if (!templates?.onDoubleClickEmpty || query !== '') return;
          openedTheListRef.current = true;
          templates.onDoubleClickEmpty(e);
          close();
        }}
        placeholder={TEST_TYPE_PLACEHOLDER}
        className={`rounded-lg border px-2.5 py-2 text-sm max-w-sm ${fieldCls}`}
      />
      {/* D25/F6 — the doctor's lab-test templates sit between the search box
          and the catalogue chips: templates first, same order as every other
          field, filtered by the same query. */}
      {templateBlock}
      {/* FIX-4 — a `position: fixed` panel takes no flow height; this spacer
          reserves its measured height so the chips below are never painted
          (and never click-intercepted) underneath it. */}
      {floating && panelHeight > 0 && (
        <div aria-hidden="true" data-testid="lab-template-group-spacer" style={{ height: panelHeight }} />
      )}
      {matches.length > 0 && (
        <ul data-testid="lab-list-picker-results" className="flex flex-wrap gap-1.5 list-none p-0 m-0">
          {matches.map((t) => (
            <li key={t.procedure_id}>
              <button
                type="button"
                onClick={() => onPick(t)}
                className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${rowCls} ${
                  isDark ? 'border-slate-600' : 'border-slate-300'
                }`}
              >
                + {t.name}
                {t.code ? <span className={`ml-1 font-normal ${mutedCls}`}>{t.code}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
      {/* Searched, nothing matched. Gated on a non-empty query (QuickSearchAdd's
          shape) so it can't fire before the doctor has typed anything. */}
      {query.trim() && matches.length === 0 && (
        <span className={`text-xs italic ${mutedCls}`}>
          {TEST_OUTSIDE_HINT}
        </span>
      )}
    </div>
  );
}
